import React from 'react';
import { Header } from '@/components/layout/Header';
import { Badge } from '@/components/ui/badge';
import { BenchmarkSnapshot } from '@/components/competitive/BenchmarkSnapshot';
import { EvolutionChart } from '@/components/competitive/EvolutionChart';
import { StrategicSummary } from '@/components/competitive/StrategicSummary';
import { MomentumIndicator } from '@/components/competitive/MomentumIndicator';

// Page Header Component
const PageHeader = () => (
  <div className="mb-10" data-testid="page-header">
    <div className="flex items-center gap-3 mb-2">
      <Badge className="bg-primary/10 text-primary border-primary/20">CONFIDENTIAL</Badge>
      <Badge className="bg-muted/50 text-muted-foreground border-border/50">CONCACAF</Badge>
    </div>
    <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
      Competitive Intelligence – CONCACAF Benchmarking
    </h1>
    <p className="text-muted-foreground mt-2">
      U.S. Soccer Federation | Strategic Competitive Analysis: USA vs Mexico vs Canada
    </p>
  </div>
);

// Assessment Info Component
const AssessmentInfo = () => (
  <div className="mb-8 p-4 rounded-lg bg-muted/30 border border-border/50">
    <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
      <div>
        <span className="text-muted-foreground">Assessment Date: </span>
        <span className="font-medium text-foreground">
          {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </span>
      </div>
      <div>
        <span className="text-muted-foreground">Scope: </span>
        <span className="font-medium text-foreground">CONCACAF National Teams</span>
      </div>
      <div>
        <span className="text-muted-foreground">Classification: </span>
        <span className="font-medium text-foreground">Board-Level Intelligence</span>
      </div>
    </div>
  </div>
);

// Footer Disclaimer Component
const FooterDisclaimer = () => (
  <div className="mt-10 p-6 rounded-lg bg-muted/20 border border-border/50">
    <h3 className="text-sm font-semibold text-foreground mb-3">Important Disclaimer</h3>
    <p className="text-xs text-muted-foreground leading-relaxed">
      The competitive intelligence, benchmarking data, and strategic assessments presented in this report 
      are derived from publicly available market signals, historical performance data, and proprietary 
      analytical models. All valuations are directional indicators intended for strategic planning purposes 
      only. Actual brand values may vary based on market conditions, tournament outcomes, and other external 
      factors. This document is intended solely for internal strategic planning and board-level decision-making 
      within the U.S. Soccer Federation. Distribution beyond authorized recipients requires written consent.
    </p>
    <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
      <div className="text-xs text-muted-foreground">
        <span className="font-semibold text-primary">YBV</span> Brand Intelligence Platform
      </div>
      <div className="text-xs text-muted-foreground">
        ISO 10668 Aligned | BSI-Aligned Methodology | Interbrand-Aligned Valuation
      </div>
    </div>
  </div>
);

// Main Competitive Intelligence Page
export default function CompetitiveIntelligence() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <PageHeader />
          <AssessmentInfo />
          <BenchmarkSnapshot />
          <EvolutionChart />
          <StrategicSummary />
          <MomentumIndicator />
          <FooterDisclaimer />
        </div>
      </main>
    </div>
  );
}
